import React, { ReactElement } from "react";
import * as palette from "@govlaunch/palette";
import { LinkedinIcon, TwitterIcon, FacebookIcon } from "react-share";
import InstagramIcon from "~/components/icons/InstagramIcon";
import YoutubeIcon from "~/components/icons/YoutubeIcon";
import { Margin } from "~/components/spacings";
import ProfilePicture from "@govlaunch/profile-picture";
import { IVendorFieldsFragment } from "~/lib/queries/fragments/__generated__/VendorFields.generated";
import Website from "~/components/profile/Website";
import getHostname from "~/lib/utils/getHostname";
import SponsorshipBadges from "~/components/vendor/profile/SponsorshipBadges";
import { Anchor } from "@govlaunch/web";
import { FormattedMessage } from "react-intl";
import useIsMobile from "~/lib/hooks/useIsMobile";

interface IVendorInfoProps {
  vendor: IVendorFieldsFragment;
  preview?: "current" | "makers" | null | undefined;
  translationButton?: () => ReactElement | null;
}

export default function VendorInfo({ vendor, preview, translationButton }: IVendorInfoProps) {
  const isPreviewingAsBasic = preview === "current";
  const isPreviewingAsMaker = preview === "makers";
  const canSeeExtendedInfo = (vendor.plan.limits.enrichedProfile || isPreviewingAsMaker) && !isPreviewingAsBasic;

  const isMobile = useIsMobile();

  return (
    <div
      css={{
        display: "grid",
        justifyItems: "center",
        backgroundColor: palette.white,
        borderRadius: 5,
        border: `solid 1px ${palette.lightestGray}`,
        overflow: "hidden",
      }}
    >
      {vendor.usersCanRequestContentTranslation && translationButton && (
        <div
          css={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            marginTop: isMobile ? "5px" : 0,
            marginRight: isMobile ? "5px" : 0,
            marginBottom: "-15px",
          }}
        >
          {translationButton()}
        </div>
      )}

      <Margin mt={24}>
        <div css={{ position: "relative" }}>
          <ProfilePicture image={vendor.logo} name={vendor.name} size={100} />

          {vendor.sponsorships.length > 0 && (
            <SponsorshipBadges sponsorships={vendor.sponsorships} vendorLogoSize={100} />
          )}
        </div>
      </Margin>

      <div
        css={{
          margin: "0 15px",
          textAlign: "center",
        }}
      >
        <Margin mt={12}>
          <h1
            css={{
              margin: 0,
              color: palette.text,
              fontSize: 26,
            }}
          >
            {vendor.name}
          </h1>
        </Margin>

        {(vendor.yearFounded || vendor.numberOfEmployees) && (
          <Margin mt={4}>
            <div
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: palette.mediumGray,
                fontSize: 14,
              }}
            >
              {vendor.yearFounded && (
                <FormattedMessage
                  defaultMessage="Founded in {year}"
                  id="ZpCZYw"
                  values={{ year: vendor.yearFounded }}
                />
              )}{" "}
              {vendor.yearFounded && vendor.numberOfEmployees && canSeeExtendedInfo && <Margin mx={3}>|</Margin>}
              {vendor.numberOfEmployees && canSeeExtendedInfo && (
                <FormattedMessage
                  defaultMessage="Contains {count, plural, one {# employee} other {# employees}}"
                  id="NxDw14"
                  values={{ count: vendor.numberOfEmployees }}
                />
              )}
            </div>
          </Margin>
        )}

        {vendor.description && (
          <Margin mt={12}>
            <p
              css={{
                color: palette.mediumGray,
              }}
            >
              {vendor.description}
            </p>
          </Margin>
        )}

        {vendor.slogan && canSeeExtendedInfo && (
          <Margin mt={18}>
            <div
              css={{
                display: "grid",
                gridColumnGap: 8,
                gridAutoFlow: "column",
                justifyContent: "center",
                color: palette.sealBlue,
                fontStyle: "italic",
              }}
            >
              <span>"{vendor.slogan}"</span>
            </div>
          </Margin>
        )}
      </div>

      {canSeeExtendedInfo &&
        vendor.socialMedia &&
        (vendor.socialMedia.facebook ||
          vendor.socialMedia.instagram ||
          vendor.socialMedia.linkedin ||
          vendor.socialMedia.twitter ||
          vendor.socialMedia.youtube) && (
          <Margin mt={18}>
            <div
              css={{
                display: "grid",
                gridColumnGap: 18,
                gridAutoFlow: "column",
                justifyContent: "center",
              }}
            >
              {vendor.socialMedia.facebook && (
                <Anchor href={vendor.socialMedia.facebook} external="outside-redirect">
                  <FacebookIcon size={32} round={true} />
                </Anchor>
              )}

              {vendor.socialMedia.instagram && (
                <Anchor href={vendor.socialMedia.instagram} external="outside-redirect">
                  <InstagramIcon height={32} width={32} />
                </Anchor>
              )}

              {vendor.socialMedia.linkedin && (
                <Anchor href={vendor.socialMedia.linkedin} external="outside-redirect">
                  <LinkedinIcon size={32} round={true} />
                </Anchor>
              )}

              {vendor.socialMedia.twitter && (
                <Anchor href={vendor.socialMedia.twitter} external="outside-redirect">
                  <TwitterIcon size={32} round={true} />
                </Anchor>
              )}

              {vendor.socialMedia.youtube && (
                <Anchor href={vendor.socialMedia.youtube} external="outside-redirect">
                  <YoutubeIcon height={32} width={32} />
                </Anchor>
              )}
            </div>
          </Margin>
        )}

      {vendor.site && !vendor.contactPage && !vendor.businessEmail && (
        <div
          css={{
            width: "100%",
            padding: 15,
            borderTop: `solid 1px ${palette.lightestGray}`,
            marginTop: 18,
          }}
        >
          <Website url={vendor.site} />{" "}
        </div>
      )}

      {vendor.site && (vendor.contactPage || vendor.businessEmail) && (
        <div
          css={{
            width: "100%",
            padding: 15,
            borderTop: `solid 1px ${palette.lightestGray}`,
            marginTop: 18,
            display: "flex",
            overflow: "hidden",
          }}
        >
          <Margin mr={22}>
            <span
              css={{
                fontWeight: 600,
                fontSize: 14,
                display: "flex",
              }}
            >
              <FormattedMessage defaultMessage="Website" id="JkLHGw" />
            </span>

            <Anchor
              href={vendor.site}
              external="outside-govlaunch"
              css={{
                color: palette.darkGray,
                userSelect: "none",
                fontSize: 14,

                "&:focus, &:visited": {
                  color: palette.darkGray,
                },

                "&:hover": {
                  color: palette.darkGray,
                  borderBottom: `solid 1px ${palette.sealBlue}`,
                },
              }}
            >
              {getHostname(vendor.site)}
            </Anchor>
          </Margin>

          {vendor.contactPage && canSeeExtendedInfo && (
            <div>
              <span
                css={{
                  fontWeight: 600,
                  fontSize: 14,
                  display: "flex",
                }}
              >
                <FormattedMessage defaultMessage="Contact page" id="G5vdr6" />
              </span>

              <Anchor
                href={vendor.contactPage}
                external="outside-govlaunch"
                css={{
                  color: palette.darkGray,
                  userSelect: "none",
                  fontSize: 14,

                  "&:focus, &:visited": {
                    color: palette.darkGray,
                  },

                  "&:hover": {
                    color: palette.darkGray,
                    borderBottom: `solid 1px ${palette.sealBlue}`,
                  },
                }}
              >
                {getHostname(vendor.contactPage)}
              </Anchor>
            </div>
          )}

          {vendor.businessEmail && canSeeExtendedInfo && (
            <div css={{ maxWidth: "75%" }}>
              <span
                css={{
                  fontWeight: 600,
                  fontSize: 14,
                  display: "flex",
                }}
              >
                <FormattedMessage defaultMessage="Contact" id="zFegDD" />
              </span>

              <a
                href={`mailto:${vendor.businessEmail}`}
                css={{
                  color: palette.darkGray,
                  userSelect: "none",
                  fontSize: 14,
                  overflowWrap: "break-word",

                  "&:focus, &:visited": {
                    color: palette.darkGray,
                  },

                  "&:hover": {
                    color: palette.darkGray,
                    borderBottom: `solid 1px ${palette.sealBlue}`,
                  },
                }}
              >
                {vendor.businessEmail}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
